'use client';

import ImageSlider from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/components/ImageSlider";
import { MediaRef } from "@/app/__to_be_integrated/_____components/image/enhanced-image/types/types";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { ChangeEvent, ReactNode } from "react";
import { createGlassEffect } from "@/app/theme/theme-extensions";
import useIsDesktop from "@/hooks/useDesktop";
import { signatureBorderStyle } from "@/app/theme/theme-tokens";
import ScrollDownArrows from "@/app/features/shared/__to_be_integrated/ui/ScrollDownArrows";
import clsx from "clsx";
import { scrollToIntroAnchor } from "@/app/features/shared/__to_be_integrated/ui/IntroAnchor";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";

interface SlideshowIntro {
    title: string;
    subtitle?: string;
    subtitle2?: string;
    images: MediaRef[];
    isEditing?: boolean;
    onTitleChanged?: (title: string) => void;
    onSubtitleChanged?: (subtitle: string) => void;
    startChildren?: ReactNode;
    heightPreset?: 'full' | 'reduced';
    textCommitMode?: 'change' | 'blur';
}

export default function SlideshowIntro({
    title,
    subtitle,
    startChildren,
    images,
    onTitleChanged,
    onSubtitleChanged,
    isEditing = false,
    heightPreset = 'full',
    textCommitMode = 'change',
}: SlideshowIntro) {

    const isDesktop = useIsDesktop();

    if (!images || images.length === 0) {
        images = [PLACEHOLDER_IMAGE];
    }

    const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (onTitleChanged) {
            onTitleChanged(event.target.value);
        }
    };

    const handleSubtitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (onSubtitleChanged) {
            onSubtitleChanged(event.target.value);
        }
    };

    return (
        <span className="relative">
            <Box className={clsx(
                "slideshow-intro-root relative w-screen bottom-fade",
                heightPreset === 'full' ? "h-full-header-aware" : "h-[60vh]"
            )}>
                <Box
                    className={clsx(
                        "slideshow-intro-content absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] max-w-[90vw] text-center cursor-pointer z-[150]",
                        isEditing ? "p-12 min-w-[50%]" : "py-4 px-8",
                    )}
                    onClick={scrollToIntroAnchor}
                >
                    <Box
                        className={clsx(
                            'transition-transform duration-300 ease-out',
                            isEditing ? "p-12 min-w-[50%]" : "py-4 px-8"
                        )}
                        sx={(theme) => ({
                            ...signatureBorderStyle,
                            ...createGlassEffect(theme),
                            '&:hover': {
                                transform: 'scale(1.05)',
                            },
                        })}

                    >
                        {startChildren}
                        {isEditing
                            ? (
                                <Stack spacing={2}>
                                    <TextField
                                        key={`title-${title}`}
                                        placeholder="Titel"
                                        {...(textCommitMode === 'blur'
                                            ? {
                                                defaultValue: title || '',
                                                onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                                                    if (event.target.value !== title) onTitleChanged?.(event.target.value);
                                                },
                                            }
                                            : {
                                                value: title || '',
                                                onChange: handleTitleChange,
                                            })}
                                        fullWidth
                                    />
                                    <TextField
                                        key={`subtitle-${subtitle}`}
                                        placeholder="Untertitel"
                                        {...(textCommitMode === 'blur'
                                            ? {
                                                defaultValue: subtitle || '',
                                                onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                                                    if (event.target.value !== (subtitle || '')) onSubtitleChanged?.(event.target.value);
                                                },
                                            }
                                            : {
                                                value: subtitle || '',
                                                onChange: handleSubtitleChange,
                                            })}
                                        fullWidth
                                    />
                                </Stack>
                            )
                            : (
                                <Stack spacing={2}>
                                    <Typography
                                        variant={isDesktop ? "h1" : "h2"}
                                        component="h1"
                                        color="text.primary"
                                        sx={{ mb: 0, overflowWrap: 'break-word' }}
                                    >
                                        {title}
                                    </Typography>
                                    {subtitle && (
                                        <Typography
                                            variant={isDesktop ? "subtitle2" : "h4"}
                                            component="h2"
                                            color="text.primary"
                                        >
                                            {subtitle}
                                        </Typography>
                                    )}
                                </Stack>
                            )
                        }
                    </Box>
                    </Box>
                {images.length > 1
                    ? <ImageSlider images={images} />
                    : (
                        <MediaView
                            src={images[0] || '/assets/placeholder.webp'}
                            zoomOnHover={false}
                            slots={{
                                root: {
                                    style: {
                                        width: '100%',
                                        height: '100%',
                                    }
                                },
                                image: {
                                    objectFit: 'cover',
                                }
                            }}
                            fill
                        />
                    )
                }

            </Box>
            <ScrollDownArrows />
        </span>
    )
}
